<template>
  <!-- Льготы используются в личных делах и могут участвовать в этапе рейтинга. -->
  <v-card class="admission-privileges" elevation="0">
    <v-card-title class="admission-privileges__header">
      <div>
        <h2 class="admission-privileges__title">Льготы</h2>
        <p class="admission-privileges__description">
          Настройте льготные категории, доступные при оформлении абитуриента.
        </p>
      </div>

      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        :disabled="loading"
        @click="openEditor()"
      >
        Добавить льготу
      </v-btn>
    </v-card-title>

    <v-card-text class="admission-privileges__content">
      <v-alert
        v-if="error"
        class="admission-privileges__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <div v-if="loading">
        <v-skeleton-loader
          v-for="index in 4"
          :key="index"
          type="list-item-two-line"
        />
      </div>

      <div v-else-if="!items.length" class="admission-privileges__empty">
        <v-icon icon="mdi-account-star-outline" size="44" />
        <div>
          <strong>Льготы пока не добавлены</strong>
          <span>Добавьте первую льготную категорию для абитуриентов.</span>
        </div>
      </div>

      <div v-else class="admission-privileges__list">
        <article
          v-for="item in items"
          :key="item.id"
          class="admission-privileges__item"
        >
          <div class="admission-privileges__item-icon">
            <v-icon icon="mdi-account-star-outline" size="24" />
          </div>

          <div class="admission-privileges__item-content">
            <div class="admission-privileges__item-name">
              {{ item.name }}
            </div>
            <div v-if="item.printText" class="admission-privileges__item-comment">
              {{ item.printText }}
            </div>
            <v-chip
              v-if="item.usageCount"
              class="admission-privileges__usage"
              color="primary"
              variant="tonal"
              size="x-small"
            >
              Используется: {{ item.usageCount }}
            </v-chip>
          </div>

          <div class="admission-privileges__item-actions">
            <v-btn
              icon="mdi-pencil-outline"
              variant="text"
              size="small"
              aria-label="Редактировать льготу"
              @click="openEditor(item)"
            />

            <v-tooltip
              :text="item.canDelete
                ? 'Удалить льготу'
                : 'Льгота используется и не может быть удалена'"
              location="top"
            >
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-btn
                    icon="mdi-delete-outline"
                    variant="text"
                    color="error"
                    size="small"
                    aria-label="Удалить льготу"
                    :disabled="!item.canDelete"
                    @click="openDeleteDialog(item)"
                  />
                </span>
              </template>
            </v-tooltip>
          </div>
        </article>
      </div>
    </v-card-text>

    <v-dialog v-model="editorDialog" max-width="680" persistent>
      <v-card>
        <v-card-title>
          {{ editedItem.id ? 'Редактирование льготы' : 'Новая льгота' }}
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="dialogError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ dialogError }}
          </v-alert>

          <div class="admission-privileges__form">
            <v-text-field
              v-model.trim="editedItem.name"
              label="Название льготы"
              variant="outlined"
              autofocus
              required
              hide-details="auto"
              @keydown.ctrl.enter="saveItem"
            />

            <v-textarea
              v-model="editedItem.printText"
              label="Комментарий"
              hint="Если поле заполнено, этот текст может использоваться при формировании заявления."
              persistent-hint
              variant="outlined"
              rows="3"
              auto-grow
              @keydown.ctrl.enter="saveItem"
            />
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="closeEditor">
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            @click="saveItem"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>Удалить льготу?</v-card-title>
        <v-card-text>
          <v-alert
            v-if="deleteError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ deleteError }}
          </v-alert>
          <p class="mb-2">
            Льгота будет удалена из справочника приемной комиссии.
          </p>
          <strong>{{ selectedItem ? selectedItem.name : '' }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="closeDeleteDialog">
            Отмена
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deleting"
            @click="deleteItem"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'PrivilegesSection',
  data() {
    return {
      items: [],
      loading: true,
      saving: false,
      deleting: false,
      error: '',
      dialogError: '',
      deleteError: '',
      editorDialog: false,
      deleteDialog: false,
      selectedItem: null,
      editedItem: {
        id: 0,
        name: '',
        printText: ''
      }
    };
  },
  created() {
    this.loadItems();
  },
  methods: {
    loadItems() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getAdmissionPrivileges'
        )
        .then((response) => {
          this.items = response.data.items || [];
        })
        .catch((error) => {
          this.error = this.getErrorMessage(
            error,
            'Не удалось загрузить перечень льгот.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openEditor(item = null) {
      this.dialogError = '';
      this.editedItem = item
        ? {
          id: Number(item.id),
          name: item.name || '',
          printText: item.printText || ''
        }
        : {
          id: 0,
          name: '',
          printText: ''
        };
      this.editorDialog = true;
    },
    closeEditor() {
      this.editorDialog = false;
      this.dialogError = '';
    },
    saveItem() {
      const name = (this.editedItem.name || '').trim();
      const printText = (this.editedItem.printText || '').trim();

      if (!name) {
        this.dialogError = 'Введите название льготы.';
        return;
      }

      this.saving = true;
      this.dialogError = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/saveAdmissionPrivilege',
          new URLSearchParams({
            id: String(this.editedItem.id || 0),
            name,
            printText
          })
        )
        .then((response) => {
          const savedItem = response.data.item;
          const index = this.items.findIndex(
            (item) => Number(item.id) === Number(savedItem.id)
          );

          if (index === -1) {
            this.items.push(savedItem);
          } else {
            this.items.splice(index, 1, savedItem);
          }

          this.items.sort((first, second) =>
            first.name.localeCompare(second.name, 'ru')
          );
          this.closeEditor();
        })
        .catch((error) => {
          this.dialogError = this.getErrorMessage(
            error,
            'Не удалось сохранить льготу.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    openDeleteDialog(item) {
      // Связанная с абитуриентом или категорией льгота должна сохранить
      // идентификатор, поэтому интерфейс не предлагает её удаление.
      if (!item.canDelete) {
        return;
      }

      this.selectedItem = item;
      this.deleteError = '';
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteError = '';
      this.selectedItem = null;
    },
    deleteItem() {
      if (!this.selectedItem) {
        return;
      }

      this.deleting = true;
      this.deleteError = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/deleteAdmissionPrivilege',
          new URLSearchParams({ id: String(this.selectedItem.id) })
        )
        .then(() => {
          this.items = this.items.filter(
            (item) => Number(item.id) !== Number(this.selectedItem.id)
          );
          this.closeDeleteDialog();
        })
        .catch((error) => {
          this.deleteError = this.getErrorMessage(
            error,
            'Не удалось удалить льготу.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    getErrorMessage(error, fallback) {
      const responseData = error && error.response
        ? error.response.data
        : null;

      if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
      }
      if (responseData && typeof responseData.message === 'string') {
        return responseData.message;
      }

      return fallback;
    }
  }
};
</script>

<style scoped>
.admission-privileges {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.admission-privileges__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}

.admission-privileges__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}

.admission-privileges__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.admission-privileges__content {
  padding: 24px 28px 28px;
}

.admission-privileges__alert {
  margin-bottom: 20px;
}

.admission-privileges__list {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}

.admission-privileges__item {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  min-height: 72px;
  padding: 12px 14px;
  border-bottom: 1px solid #edf0f4;
}

.admission-privileges__item:last-child {
  border-bottom: 0;
}

.admission-privileges__item-icon {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 10px;
  background: #fff4e5;
  color: #b54708;
}

.admission-privileges__item-content {
  min-width: 0;
}

.admission-privileges__item-name {
  color: #344054;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.admission-privileges__item-comment {
  margin-top: 3px;
  color: #667085;
  font-size: 13px;
  line-height: 1.4;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.admission-privileges__usage {
  margin-top: 7px;
}

.admission-privileges__item-actions {
  display: flex;
  align-items: center;
}

.admission-privileges__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 190px;
  color: #667085;
}

.admission-privileges__empty strong,
.admission-privileges__empty span {
  display: block;
}

.admission-privileges__empty strong {
  margin-bottom: 4px;
  color: #344054;
}

.admission-privileges__form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

@media (max-width: 600px) {
  .admission-privileges__header {
    align-items: stretch;
    flex-direction: column;
  }

  .admission-privileges__header,
  .admission-privileges__content {
    padding-right: 18px;
    padding-left: 18px;
  }

  .admission-privileges__item {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .admission-privileges__item-icon {
    display: none;
  }
}
</style>
